/**
 * JiraPullPanel — inline-панель импорта из Jira по JQL.
 *
 * Раньше это была отдельная модалка (JiraPullModal), теперь тело встроено прямо
 * во вкладку Pull окна синхронизации, чтобы весь функционал жил в одном окне:
 * выбор сохранённого запроса, JQL, превью новых тасок и импорт.
 *
 * Flow: JQL → Preview → Select → Execute. Режимы: create_only, create_and_update.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Input,
  Button,
  Table,
  Tag,
  Segmented,
  Alert,
  Statistic,
  Space,
  Typography,
  Checkbox,
  message,
} from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  PlusCircleOutlined,
  SyncOutlined,
  MinusCircleOutlined,
  SaveOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import type { JiraPullPreviewItem } from '@lens/shared';
import { pullPreview, pullExecute, listSavedQueries, createSavedQuery, deleteSavedQuery } from './api.ts';
import type { SavedPullQuery } from './api.ts';

const { TextArea } = Input;
const { Text } = Typography;

export type PullMode = 'create_only' | 'create_and_update';

interface JiraPullPanelProps {
  /** Панель активна (вкладка Pull открыта) — грузит сохранённые запросы и авто-ищет. */
  active: boolean;
  spaceId: string;
  itemTypeId: string;
  /** Название типа записи для заголовка. */
  itemTypeName?: string;
  /** Предзаполнить JQL при активации. */
  initialJql?: string;
  /** Предзаполнить режим при активации. */
  initialMode?: PullMode;
  /** Авто-запустить поиск при активации, если JQL уже определён (напр. единственный сохранённый запрос). */
  autoSearch?: boolean;
  /** Колбэк после успешного импорта (для обновления inbox/таблицы). */
  onImported?: () => void;
}

type Phase = 'input' | 'loading' | 'preview' | 'executing' | 'done';

const ACTION_CONFIG = {
  create: { color: 'green', icon: <PlusCircleOutlined />, label: 'Создать' },
  update: { color: 'blue', icon: <SyncOutlined />, label: 'Обновить' },
  skip: { color: 'default', icon: <MinusCircleOutlined />, label: 'Пропустить' },
  no_changes: { color: 'default', icon: <CheckCircleOutlined />, label: 'Без изменений' },
} as const;

export function JiraPullPanel({
  active,
  spaceId,
  itemTypeId,
  itemTypeName,
  initialJql,
  initialMode,
  autoSearch,
  onImported,
}: JiraPullPanelProps) {
  const [jql, setJql] = useState('');
  const [mode, setMode] = useState<PullMode>('create_and_update');
  const [phase, setPhase] = useState<Phase>('input');
  const [previewItems, setPreviewItems] = useState<JiraPullPreviewItem[]>([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<
    { created: number; updated: number; skipped: number; errors: Array<{ jiraKey: string; error: string }> } | null
  >(null);

  // Saved queries
  const [savedQueries, setSavedQueries] = useState<SavedPullQuery[]>([]);
  const [activeQueryId, setActiveQueryId] = useState<string | null>(null);
  const [savingQuery, setSavingQuery] = useState(false);
  const [saveQueryName, setSaveQueryName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const autoRanRef = useRef(false);

  const reset = useCallback(() => {
    setPhase('input');
    setPreviewItems([]);
    setPreviewTotal(0);
    setSelectedKeys([]);
    setError(null);
    setResult(null);
  }, []);

  // ─── Preview ──────────────────────────────────────────────────────
  const runSearch = useCallback(
    async (jqlArg: string, modeArg: PullMode) => {
      const q = jqlArg.trim();
      if (!q) return;
      setPhase('loading');
      setError(null);
      try {
        const res = await pullPreview(spaceId, {
          jql: q,
          itemTypeId,
          fieldMapping: { summaryTarget: 'title', fields: [] },
          mode: modeArg,
          maxResults: 200,
        });
        setPreviewItems(res.items);
        setPreviewTotal(res.total);
        setSelectedKeys(
          res.items.filter((i) => i.action === 'create' || i.action === 'update').map((i) => i.jiraKey),
        );
        setPhase('preview');
      } catch (err: any) {
        setError(err.message ?? 'Ошибка поиска в Jira');
        setPhase('input');
      }
    },
    [spaceId, itemTypeId],
  );

  // Активация: грузим сохранённые запросы, при необходимости авто-ищем.
  // Деактивация: полный сброс, чтобы повторное открытие было чистым.
  useEffect(() => {
    if (!active) {
      autoRanRef.current = false;
      reset();
      setJql('');
      setActiveQueryId(null);
      setSavedQueries([]);
      setShowSaveInput(false);
      setSaveQueryName('');
      return;
    }
    let cancelled = false;
    (async () => {
      let queries: SavedPullQuery[] = [];
      try {
        queries = await listSavedQueries(spaceId, itemTypeId);
      } catch {
        /* нет конфига / нет прав — просто пустой список */
      }
      if (cancelled) return;
      setSavedQueries(queries);
      if (autoRanRef.current) return;
      autoRanRef.current = true;

      // Определяем стартовый запрос: явный initialJql → единственный сохранённый.
      let q = (initialJql ?? '').trim();
      let m: PullMode = initialMode ?? 'create_and_update';
      let activeId: string | null = null;
      const only = queries.length === 1 ? queries[0] : undefined;
      if (!q && only) {
        q = only.jql;
        m = only.mode;
        activeId = only.id;
      } else if (q) {
        const match = queries.find((x) => x.jql === q);
        activeId = match?.id ?? null;
      }
      if (q) {
        setJql(q);
        setMode(m);
        setActiveQueryId(activeId);
        if (autoSearch) void runSearch(q, m);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, spaceId, itemTypeId]);

  const handleSaveQuery = useCallback(async () => {
    if (!saveQueryName.trim() || !jql.trim()) return;
    setSavingQuery(true);
    try {
      const q = await createSavedQuery(spaceId, itemTypeId, {
        name: saveQueryName.trim(),
        jql: jql.trim(),
        mode,
      });
      setSavedQueries((prev) => [...prev, q]);
      setActiveQueryId(q.id);
      setShowSaveInput(false);
      setSaveQueryName('');
      void message.success('Запрос сохранён');
    } catch {
      void message.error('Не удалось сохранить запрос');
    } finally {
      setSavingQuery(false);
    }
  }, [saveQueryName, jql, mode, spaceId, itemTypeId]);

  const handleDeleteQuery = useCallback(
    async (queryId: string) => {
      try {
        await deleteSavedQuery(spaceId, itemTypeId, queryId);
        setSavedQueries((prev) => prev.filter((q) => q.id !== queryId));
        setActiveQueryId((cur) => (cur === queryId ? null : cur));
      } catch {
        void message.error('Не удалось удалить запрос');
      }
    },
    [spaceId, itemTypeId],
  );

  // Выбор сохранённого запроса: подставляем JQL/режим и сразу ищем.
  const handleSelectQuery = useCallback(
    (q: SavedPullQuery) => {
      setJql(q.jql);
      setMode(q.mode);
      setActiveQueryId(q.id);
      void runSearch(q.jql, q.mode);
    },
    [runSearch],
  );

  // ─── Execute ──────────────────────────────────────────────────────
  const handleExecute = useCallback(async () => {
    if (selectedKeys.length === 0) return;
    setPhase('executing');
    setError(null);
    try {
      const res = await pullExecute(spaceId, {
        jql: jql.trim(),
        itemTypeId,
        fieldMapping: { summaryTarget: 'title', fields: [] },
        mode,
        selectedKeys,
      });
      setResult(res);
      setPhase('done');
      if (res.created + res.updated > 0) {
        void message.success(`Импортировано: ${res.created} создано, ${res.updated} обновлено`);
        onImported?.();
      }
    } catch (err: any) {
      setError(err.message ?? 'Ошибка импорта');
      setPhase('preview');
    }
  }, [selectedKeys, jql, spaceId, itemTypeId, mode, onImported]);

  // ─── Preview table columns ────────────────────────────────────────
  const columns = [
    {
      title: 'Issue',
      key: 'issue',
      render: (_: unknown, record: JiraPullPreviewItem) => (
        <div>
          <Text strong>{record.jiraKey}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.summary}
          </Text>
        </div>
      ),
    },
    {
      title: 'Действие',
      key: 'action',
      width: 140,
      render: (_: unknown, record: JiraPullPreviewItem) => {
        const cfg = ACTION_CONFIG[record.action];
        return (
          <Tag color={cfg.color} icon={cfg.icon}>
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: 'Изменения',
      key: 'changes',
      width: 160,
      render: (_: unknown, record: JiraPullPreviewItem) =>
        record.changedFields?.length ? record.changedFields.join(', ') : null,
    },
  ];

  // ─── Stats ────────────────────────────────────────────────────────
  const stats = {
    create: previewItems.filter((i) => i.action === 'create').length,
    update: previewItems.filter((i) => i.action === 'update').length,
    skip: previewItems.filter((i) => i.action === 'skip' || i.action === 'no_changes').length,
  };

  const actionableCount = previewItems.filter(
    (i) => i.action === 'create' || i.action === 'update',
  ).length;

  // В режиме «только новые» уже существующие в Lens задачи приходят как skip —
  // показывать их в таблице бессмысленно, оставляем только create-и.
  const isCreateOnly = mode === 'create_only';
  const displayItems = isCreateOnly ? previewItems.filter((i) => i.action === 'create') : previewItems;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="jira-pull-panel">
      <Text strong style={{ fontSize: 13 }}>
        ⬇ Импорт новых задач из Jira{itemTypeName ? ` → ${itemTypeName}` : ''}
      </Text>

      {/* ─── Input / loading phase ───────────────────────────────── */}
      {(phase === 'input' || phase === 'loading') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Saved queries — выбор сразу ищет */}
          {savedQueries.length > 0 && (
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                <HistoryOutlined /> Сохранённые запросы
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {savedQueries.map((q) => (
                  <Tag
                    key={q.id}
                    color={q.id === activeQueryId ? 'blue' : 'default'}
                    style={{
                      cursor: 'pointer',
                      marginBottom: 2,
                      borderStyle: q.id === activeQueryId ? 'solid' : 'dashed',
                    }}
                    closable
                    onClose={(e) => {
                      e.preventDefault();
                      void handleDeleteQuery(q.id);
                    }}
                    onClick={() => handleSelectQuery(q)}
                    data-testid="pull-saved-query"
                  >
                    {q.name}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              JQL-запрос
            </Text>
            <TextArea
              rows={3}
              placeholder='project=DAEGOALS AND issuetype="Квартальная цель" AND "Отчётный период"="Q3 2026"'
              value={jql}
              onChange={(e) => {
                setJql(e.target.value);
                setActiveQueryId(null); // ручная правка — сбрасываем выделенный запрос
              }}
              disabled={phase === 'loading'}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Text>Режим:</Text>
            <Segmented
              value={mode}
              onChange={(v) => setMode(v as PullMode)}
              options={[
                { value: 'create_and_update', label: 'Создать + обновить' },
                { value: 'create_only', label: 'Только новые' },
              ]}
              disabled={phase === 'loading'}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={phase === 'loading'}
              disabled={!jql.trim()}
              onClick={() => void runSearch(jql, mode)}
              data-testid="pull-search"
            >
              Найти в Jira
            </Button>

            {jql.trim() && !showSaveInput && !activeQueryId && (
              <Button icon={<SaveOutlined />} size="small" onClick={() => setShowSaveInput(true)}>
                Сохранить запрос
              </Button>
            )}

            {showSaveInput && (
              <>
                <Input
                  size="small"
                  placeholder="Название запроса"
                  value={saveQueryName}
                  onChange={(e) => setSaveQueryName(e.target.value)}
                  onPressEnter={() => void handleSaveQuery()}
                  style={{ width: 200 }}
                  autoFocus
                />
                <Button
                  size="small"
                  type="primary"
                  loading={savingQuery}
                  disabled={!saveQueryName.trim()}
                  onClick={() => void handleSaveQuery()}
                >
                  OK
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setShowSaveInput(false);
                    setSaveQueryName('');
                  }}
                >
                  ✕
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── Preview phase ───────────────────────────────────────── */}
      {phase === 'preview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            <Statistic
              title="Создать"
              value={stats.create}
              valueStyle={{ color: 'var(--color-success)', fontSize: 20 }}
            />
            {!isCreateOnly && (
              <Statistic
                title="Обновить"
                value={stats.update}
                valueStyle={{ color: 'var(--color-info)', fontSize: 20 }}
              />
            )}
            <Statistic
              title={isCreateOnly ? 'Уже в Lens' : 'Пропустить'}
              value={stats.skip}
              valueStyle={{ color: 'var(--color-text-secondary)', fontSize: 20 }}
            />
            <Statistic title="Всего в Jira" value={previewTotal} valueStyle={{ fontSize: 20 }} />
          </div>

          {actionableCount > 0 && (
            <Checkbox
              checked={selectedKeys.length === actionableCount}
              indeterminate={selectedKeys.length > 0 && selectedKeys.length < actionableCount}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedKeys(
                    previewItems
                      .filter((i) => i.action === 'create' || i.action === 'update')
                      .map((i) => i.jiraKey),
                  );
                } else {
                  setSelectedKeys([]);
                }
              }}
            >
              Выбрать все ({actionableCount})
            </Checkbox>
          )}

          <Table
            dataSource={displayItems}
            columns={columns}
            rowKey="jiraKey"
            size="small"
            pagination={displayItems.length > 50 ? { pageSize: 50 } : false}
            scroll={{ y: 320 }}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: (keys) => setSelectedKeys(keys as string[]),
              getCheckboxProps: (record: JiraPullPreviewItem) => ({
                disabled: record.action === 'skip' || record.action === 'no_changes',
              }),
            }}
          />

          <Space>
            <Button onClick={reset}>← Изменить запрос</Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled={selectedKeys.length === 0}
              onClick={() => void handleExecute()}
              data-testid="pull-execute"
            >
              Импортировать ({selectedKeys.length})
            </Button>
          </Space>
        </div>
      )}

      {/* ─── Executing phase ─────────────────────────────────────── */}
      {phase === 'executing' && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <SyncOutlined spin style={{ fontSize: 32, color: 'var(--color-info)' }} />
          <div style={{ marginTop: 12 }}>
            <Text>Импортируем {selectedKeys.length} записей...</Text>
          </div>
        </div>
      )}

      {/* ─── Done phase ──────────────────────────────────────────── */}
      {phase === 'done' && result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Alert
            type={result.errors.length > 0 ? 'warning' : 'success'}
            message={
              result.errors.length > 0
                ? `Импорт завершён с ошибками (${result.errors.length})`
                : 'Импорт успешно завершён'
            }
            description={`Создано: ${result.created} | Обновлено: ${result.updated} | Пропущено: ${result.skipped}`}
          />
          {result.errors.length > 0 && (
            <Table
              dataSource={result.errors}
              columns={[
                { title: 'Issue', dataIndex: 'jiraKey', key: 'jiraKey', width: 140 },
                { title: 'Ошибка', dataIndex: 'error', key: 'error' },
              ]}
              rowKey="jiraKey"
              size="small"
              pagination={false}
            />
          )}
          <Space>
            <Button
              onClick={() => {
                reset();
                setActiveQueryId(null);
              }}
            >
              Импортировать ещё
            </Button>
          </Space>
        </div>
      )}

      {/* ─── Error ───────────────────────────────────────────────── */}
      {error && (
        <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginTop: 4 }} />
      )}
    </div>
  );
}
